import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, BarChart3, Building2, ClipboardList, FileText, Inbox, Link2, Route, UserMinus, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Loading } from "@/components/common";
import { MetricCard, Panel, PanelHeader } from "./ui";
import { wardScope } from "@/lib/patient";
import { AgendaPanel, AttentionPanel } from "./ConversationsHome";
import { ConversationsOverviewPanel, CounselorFilter } from "./ConversationsOverview";
import { MyMeetingsToday } from "./MeetingGrid";
import { ConversationMetricCards, useConversationMetrics } from "./ConversationsMetrics";

const SHORTCUTS = [
  { key: "patients", to: "/portal/pacientes", label: "Pacientes", hint: "Episódios, boletins e medicamentos", icon: Users },
  { key: "grants", to: "/portal/vinculos", label: "Vínculos", hint: "Autorizações da rede de apoio", icon: Link2 },
  { key: "threads", to: "/portal/perguntas", label: "Perguntas anônimas", hint: "Responder o canal público", icon: Inbox },
  { key: "requests", to: "/portal/solicitacoes", label: "Solicitações", hint: "Admissões e contatos", icon: ClipboardList },
  { key: "content", to: "/portal/conteudo", label: "Conteúdo", hint: "Publicações do app público", icon: FileText },
  { key: "facility", to: "/portal/estrutura", label: "Estrutura", hint: "Fotos e ambientes da clínica", icon: Building2 },
  { key: "metrics", to: "/portal/metricas", label: "Métricas", hint: "Indicadores agregados", icon: BarChart3 },
];

const OPEN_REQUEST_STATUS = ["new", "assigned", "contact_attempted", "contacted"];
const OPEN_THREAD_STATUS = ["received", "in_analysis", "waiting_for_user"];

export default function PortalOverview() {
  const { api, user } = useAuth();
  const [counts, setCounts] = useState(null);
  const [overdue, setOverdue] = useState(null);
  const scope = wardScope(user);
  const [ward, setWard] = useState(scope.value);
  const [teamGaps, setTeamGaps] = useState(null);
  const conversationMetrics = useConversationMetrics("7d", ward);
  const isCounselor = user?.staff_type === "conselheiro";
  const canFollowConversations = ["administracao", "diretoria"].includes(user?.staff_type);
  const [conversationCounselor, setConversationCounselor] = useState("");

  useEffect(() => {
    if (!(user?.permissions || []).includes("timeline")) {
      setOverdue(null);
      return;
    }
    api
      .get("/portal/timeline/overdue", { params: { ward } })
      .then(({ data }) => setOverdue(data.totals))
      .catch(() => setOverdue(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.permissions, ward]);

  useEffect(() => {
    api
      .get("/portal/patients/overview", { params: { ward } })
      .then(({ data }) => setTeamGaps(data))
      .catch(() => setTeamGaps(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ward]);

  useEffect(() => {
    const safe = async (path, params) => {
      try {
        const { data } = await api.get(path, { params });
        return Array.isArray(data) ? data : [];
      } catch {
        return null;
      }
    };
    (async () => {
      const [patients, grants, threads, requests] = await Promise.all([
        safe("/portal/patients"),
        safe("/portal/access-grants"),
        safe("/portal/anonymous-threads"),
        safe("/portal/evaluation-requests"),
      ]);
      setCounts({
        patients: patients?.length ?? null,
        grants: grants?.filter((g) => g.status === "active").length ?? null,
        threads: threads?.filter((t) => OPEN_THREAD_STATUS.includes(t.status)).length ?? null,
        requests: requests?.filter((r) => OPEN_REQUEST_STATUS.includes(r.status)).length ?? null,
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (counts === null) return <Loading label="Carregando visão geral…" />;

  const metrics = [
    { key: "patients", label: "Pacientes acompanhados", value: counts.patients, hint: "Casos com acesso liberado para você", icon: Users },
    { key: "grants", label: "Vínculos ativos", value: counts.grants, hint: "Autorizações válidas da rede de apoio", icon: Link2 },
    { key: "threads", label: "Perguntas a responder", value: counts.threads, hint: "Canal anônimo aguardando a equipe", icon: Inbox, alert: (counts.threads || 0) > 0 },
    { key: "requests", label: "Solicitações abertas", value: counts.requests, hint: "Contatos ainda sem desfecho", icon: ClipboardList, alert: (counts.requests || 0) > 0 },
  ].filter((m) => m.value !== null);

  return (
    <div data-testid="portal-overview-page" className="space-y-8">
      <Panel testId="portal-welcome" padded={false} className="overflow-hidden">
        <div className="grid gap-0 md:grid-cols-[1fr_260px]">
          <div className="p-7">
            <p className="er-label text-[12px] tracking-[0.16em]">Operação clínica interna</p>
            <h1 className="mt-2.5 font-display text-[28px] font-semibold leading-[1.2] tracking-tight text-ink-900">
              Olá, {String(user?.name || "").split(" ")[0]}
            </h1>
            <p className="mt-2.5 max-w-xl text-[15px] leading-relaxed text-ink-600">
              Tudo o que a equipe publica aqui chega aos aplicativos com autorização revalidada e registro em auditoria.
            </p>
          </div>
          <img
            data-testid="portal-welcome-banner"
            src={`${process.env.REACT_APP_BACKEND_URL}/api/v1/media/estrutura/ADV6816-screen.jpg`}
            alt="Área arborizada da Clínica Erimus, em Atibaia"
            className="h-40 w-full object-cover object-[68%_45%] md:h-full"
          />
        </div>
      </Panel>

      <section data-testid="portal-metrics-summary" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map((m) => (
          <MetricCard
            key={m.key}
            testId={`overview-metric-${m.key}`}
            label={m.label}
            value={m.value}
            hint={m.hint}
            icon={m.icon}
            alert={m.alert}
          />
        ))}
      </section>

      <MyMeetingsToday />

      {isCounselor ? (
        <div className="grid gap-5 xl:grid-cols-2">
          <AgendaPanel ward={ward} />
          <AttentionPanel ward={ward} />
        </div>
      ) : null}

      {canFollowConversations ? (
        <section data-testid="overview-conversations-follow" className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-base font-semibold text-ink-900 md:text-lg">
              Conversas com Conselheiros
            </h2>
            <CounselorFilter value={conversationCounselor} onChange={setConversationCounselor} ward={ward} />
          </div>
          <div className="grid gap-5 xl:grid-cols-2">
            <AgendaPanel ward={ward} counselorId={conversationCounselor} />
            <AttentionPanel ward={ward} counselorId={conversationCounselor} />
          </div>
          <ConversationsOverviewPanel ward={ward} counselorId={conversationCounselor} />
        </section>
      ) : null}

      <Panel testId="overview-conversations-panel">
        <PanelHeader
          label="Conversas com Conselheiros"
          title="Acompanhamento semanal das conversas"
          hint="Somente pacientes ativos da ala autorizada para o seu acesso."
          actions={
            <Link
              to="/portal/metricas"
              data-testid="overview-conversations-link"
              className="text-[14px] font-semibold text-sage-700 hover:underline"
            >
              Ver detalhes
            </Link>
          }
        />
        <div className="mt-5">
          <ConversationMetricCards data={conversationMetrics || null} />
        </div>
      </Panel>

      <Panel testId="overview-care-gaps-panel">
        <PanelHeader
          label="Equipe responsável"
          title="Pacientes sem profissional atribuído"
          hint="Contagem dentro da ala autorizada para o seu acesso."
        />
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {scope.locked ? (
            <span
              data-testid="overview-ward-scope"
              className="inline-flex h-10 items-center rounded-xl bg-sand px-3.5 text-[14px] font-semibold text-ink-700"
            >
              {scope.label}
            </span>
          ) : (
            <select
              data-testid="overview-filter-ward"
              aria-label="Ala"
              value={ward}
              onChange={(e) => setWard(e.target.value)}
              className="h-10 rounded-xl border border-border bg-white px-3.5 text-[14px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15"
            >
              <option value="all">Todas</option>
              <option value="MASCULINA">Masculina</option>
              <option value="FEMININA">Feminina</option>
            </select>
          )}
          <span data-testid="overview-care-gaps-total" className="text-[13.5px] text-ink-500">
            {teamGaps ? `${teamGaps.total_patients} paciente(s) ativo(s) considerados` : "Carregando…"}
          </span>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <MetricCard
            testId="overview-without-counselor"
            label="Pacientes sem Conselheiro"
            value={teamGaps?.without_counselor ?? "—"}
            hint="Sem conselheiro vinculado"
            icon={UserMinus}
            alert={(teamGaps?.without_counselor || 0) > 0}
          />
          <MetricCard
            testId="overview-without-psychologist"
            label="Pacientes sem Psicólogo(a)"
            value={teamGaps?.without_psychologist ?? "—"}
            hint="Sem psicólogo(a) vinculado"
            icon={UserMinus}
            alert={(teamGaps?.without_psychologist || 0) > 0}
          />
          <MetricCard
            testId="overview-without-psychiatrist"
            label="Pacientes sem Psiquiatra"
            value={teamGaps?.without_psychiatrist ?? "—"}
            hint="Sem psiquiatra vinculado"
            icon={UserMinus}
            alert={(teamGaps?.without_psychiatrist || 0) > 0}
          />
        </div>
      </Panel>

      {overdue ? (
        <Panel testId="overview-overdue-panel">
          <PanelHeader
            label="Prazos atrasados"
            title="Linha do Tempo — atrasos gerais"
            hint="Dias corridos a partir da Data de internação, no fuso America/Sao_Paulo."
          />
          <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              testId="overview-overdue-patients"
              label="Pacientes atrasados"
              value={overdue.patients}
              hint="Casos com pelo menos um prazo vencido"
              icon={AlertTriangle}
              alert={overdue.patients > 0}
            />
            <MetricCard
              testId="overview-overdue-states"
              label="Estados atrasados"
              value={overdue.states}
              hint="Estados com prazo vencido"
              icon={AlertTriangle}
              alert={overdue.states > 0}
            />
            {(overdue.by_track || []).map((t) => (
              <MetricCard
                key={t.track}
                testId={`overview-overdue-${t.track}`}
                label={t.label}
                value={t.count}
                hint="Estados atrasados no tema"
                icon={Route}
              />
            ))}
          </div>
          <Link
            to="/portal/linha-do-tempo"
            data-testid="overview-overdue-link"
            className="mt-4 inline-flex items-center gap-2 text-[14px] font-semibold text-sage-700 hover:underline"
          >
            Abrir a Linha do Tempo <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </Panel>
      ) : null}

      <Panel testId="portal-shortcuts">
        <PanelHeader label="Áreas do portal" title="Atalhos" hint="Acesse rapidamente as rotinas do dia a dia." />
        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {SHORTCUTS.map((s) => (
            <Link
              key={s.to}
              to={s.to}
              data-testid={`overview-shortcut-${s.key}`}
              className="group flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-3.5 transition-colors hover:border-sage-600/40 hover:bg-sage-50/50"
            >
              <span className="flex items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-sand text-sage-700">
                  <s.icon size={18} aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-[15px] font-semibold text-ink-900">{s.label}</span>
                  <span className="block text-[13.5px] text-ink-500">{s.hint}</span>
                </span>
              </span>
              <ArrowRight size={17} className="shrink-0 text-ink-400 transition-transform group-hover:translate-x-0.5" />
            </Link>
          ))}
        </div>
      </Panel>
    </div>
  );
}
